import { requestPresignedUpload, uploadToR2 } from "@veltro/shared/lib/r2Storage";

// ─── Types ─────────────────────────────────────────────────────────────────

interface QueueEntry {
  id: string;
  bucket: string;
  objectKey: string;
  contentType: string;
  entityType: string;
  entityId: string;
  timestamp: number;
  retryCount: number;
  /** Base64-encoded file content for persistence in localStorage */
  fileBase64: string;
  fileSize: number;
}

// ─── Constants ─────────────────────────────────────────────────────────────

const STORAGE_KEY = "veltro_upload_queue";
const MAX_QUEUE_SIZE = 50;
const MAX_RETRIES = 3;
// ─── Queue persistence ─────────────────────────────────────────────────────

function loadQueue(): QueueEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveQueue(queue: QueueEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // localStorage full — silently drop. The user will see the upload fail.
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(base64: string): Blob {
  const [header, data] = base64.split(",");
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Add a failed upload to the retry queue.
 * Files are stored as base64 in localStorage.
 * Files too large for localStorage (~5MB) are silently dropped.
 */
export async function enqueueUpload(
  bucket: string,
  objectKey: string,
  file: File | Blob,
  contentType: string,
  entityType: string,
  entityId: string,
): Promise<void> {
  const queue = loadQueue();

  if (queue.length >= MAX_QUEUE_SIZE) {
    return; // queue full
  }

  try {
    const fileBase64 = await blobToBase64(file);
    queue.push({
      id: `uq_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      bucket,
      objectKey,
      contentType,
      entityType,
      entityId,
      timestamp: Date.now(),
      retryCount: 0,
      fileBase64,
      fileSize: file.size,
    });
    saveQueue(queue);
  } catch {
    // File too large for base64/localStorage — silently drop
  }
}

/**
 * Process all pending uploads in the queue.
 * Returns the number of successfully processed items.
 */
export async function processUploadQueue(): Promise<number> {
  const queue = loadQueue();
  if (queue.length === 0) return 0;

  const remaining: QueueEntry[] = [];
  let processed = 0;

  for (const entry of queue) {
    try {
      const blob = base64ToBlob(entry.fileBase64);
      const { uploadUrl } = await requestPresignedUpload(
        entry.bucket,
        entry.objectKey,
        entry.contentType,
        entry.fileSize,
      );
      await uploadToR2(uploadUrl, blob, entry.contentType);
      processed++;
    } catch {
      if (entry.retryCount < MAX_RETRIES) {
        remaining.push({ ...entry, retryCount: entry.retryCount + 1 });
      }
      // Items exceeding MAX_RETRIES are dropped
    }
  }

  saveQueue(remaining);
  return processed;
}

/**
 * Get the current number of pending uploads.
 */
export function getPendingUploadCount(): number {
  return loadQueue().length;
}

/**
 * Clear all pending uploads from the queue.
 */
export function clearUploadQueue(): void {
  localStorage.removeItem(STORAGE_KEY);
}

// ─── Auto-retry listeners ──────────────────────────────────────────────────

let listenersInstalled = false;

/**
 * Install global listeners to auto-retry pending uploads.
 * Call once at app startup. Listens for:
 * - `online` event (network reconnect)
 * - `visibilitychange` (tab becomes visible)
 */
export function installUploadQueueListeners(): void {
  if (listenersInstalled) return;
  listenersInstalled = true;

  window.addEventListener("online", () => {
    processUploadQueue();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      processUploadQueue();
    }
  });
}
